import React, { useState, useEffect } from "react";
import axios from "axios";
import EmojiPicker from "emoji-picker-react";
import { useAuth } from "../../AuthContext";
import { buildApiUrl } from "../../config";  // ✅ Import config
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";

// ✅ Configure custom font sizes for Quill
const Size = Quill.import('formats/size');
Size.whitelist = ['10px', '11px', '12px', '14px', '16px', '18px', '20px', '24px'];
Quill.register(Size, true);

const EditPost = ({ post, onClose, onPostUpdated, authorInfo, studentForm, targetForms }) => {
  const { role, teacherEmail, authorInfo: contextAuthorInfo } = useAuth();
  
  // ✅ Improved user resolution logic matching CreatePost
  const getUserEmail = () => {
    return authorInfo?.email || 
           authorInfo?.name || 
           contextAuthorInfo?.email || 
           contextAuthorInfo?.name ||
           teacherEmail || 
           localStorage.getItem('userEmail') ||
           "Hisham Shehadeh"; // Better default name
  };

  const currentUserEmail = getUserEmail();
  
  console.log("🔍 EditPost email resolution:", {
    authorInfo,
    contextAuthorInfo,
    teacherEmail,
    role,
    resolvedEmail: currentUserEmail
  });
  const [title, setTitle] = useState(post.title || "");
  const [content, setContent] = useState(post.content || "");
  const [selectedForms, setSelectedForms] = useState(post.target_forms || []);
  const [category, setCategory] = useState(post.category || "school-announcement");
  const [files, setFiles] = useState([]);
  const [existingFiles, setExistingFiles] = useState(post.media_urls || []);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  
  // ✅ Pupil tagging states
  const [availablePupils, setAvailablePupils] = useState([]);
  const [taggedPupils, setTaggedPupils] = useState(post.tagged_pupils || []);
  const [showPupilTagging, setShowPupilTagging] = useState(false);
  const [pupilSearchTerm, setPupilSearchTerm] = useState("");
  const [loadingPupils, setLoadingPupils] = useState(false);

  // ✅ NEW: Text formatting ref
  const textareaRef = React.useRef(null);

  // Fetch available pupils
  useEffect(() => {
    const fetchPupils = async () => {
      setLoadingPupils(true);
      try {
        const response = await fetch(buildApiUrl("/api/posts/pupils/search"));
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        const data = await response.json();
        setAvailablePupils(Array.isArray(data.pupils) ? data.pupils : []);
        console.log("📚 Loaded pupils for edit:", data.pupils?.length || 0);
      } catch (error) {
        console.error("❌ Error loading pupils:", error.message);
        setAvailablePupils([]);
      } finally {
        setLoadingPupils(false);
      }
    };
    
    fetchPupils();
  }, []);

  // Search pupils when search term changes
  useEffect(() => {
    if (pupilSearchTerm.length >= 2) {
      const searchPupils = async () => {
        try {
          const response = await fetch(
            buildApiUrl(`/api/posts/pupils/search?q=${encodeURIComponent(pupilSearchTerm)}`)
          );
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }
          const data = await response.json();
          setAvailablePupils(Array.isArray(data.pupils) ? data.pupils : []);
        } catch (error) {
          console.error("❌ Pupil search error:", error.message);
          setAvailablePupils([]);
        }
      };
      
      const debounceTimer = setTimeout(searchPupils, 300);
      return () => clearTimeout(debounceTimer);
    }
  }, [pupilSearchTerm]);

  // ✅ Quill editor configuration
  const quillModules = {
    toolbar: [
      [{ 'size': ['10px', '11px', '12px', '14px', '16px', '18px', '20px', '24px'] }],
      ['bold', 'italic', 'underline'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'align': [] }],
      ['clean']
    ]
  };

  const quillFormats = [
    'size', 'bold', 'italic', 'underline', 'color', 'background', 'align'
  ];

  // Add custom CSS for font sizes
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="10px"]::before,
      .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="10px"]::before {
        content: '10px';
        font-size: 10px !important;
      }
      .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="11px"]::before,
      .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="11px"]::before {
        content: '11px';
        font-size: 11px !important;
      }
      .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="12px"]::before,
      .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="12px"]::before {
        content: '12px';
        font-size: 12px !important;
      }
      .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="14px"]::before,
      .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="14px"]::before {
        content: '14px';
        font-size: 14px !important;
      }
      .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="16px"]::before,
      .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="16px"]::before {
        content: '16px';
        font-size: 16px !important;
      }
      .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="18px"]::before,
      .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="18px"]::before {
        content: '18px';
        font-size: 18px !important;
      }
      .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="20px"]::before,
      .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="20px"]::before {
        content: '20px';
        font-size: 20px !important;
      }
      .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="24px"]::before,
      .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="24px"]::before {
        content: '24px';
        font-size: 24px !important;
      }
      .ql-editor .ql-size-10px { font-size: 10px; }
      .ql-editor .ql-size-11px { font-size: 11px; }
      .ql-editor .ql-size-12px { font-size: 12px; }
      .ql-editor .ql-size-14px { font-size: 14px; }
      .ql-editor .ql-size-16px { font-size: 16px; }
      .ql-editor .ql-size-18px { font-size: 18px; }
      .ql-editor .ql-size-20px { font-size: 20px; }
      .ql-editor .ql-size-24px { font-size: 24px; }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!title.trim() || !content.trim()) {
      alert("Please fill in both title and content.");
      return;
    }

    setIsSubmitting(true);

    try {
      const formData = new FormData();
      formData.append("title", title);
      formData.append("content", content);
      
      // Convert arrays to comma-separated strings to match backend API
      formData.append("target_forms", selectedForms.join(","));
      formData.append("tagged_pupils", taggedPupils.join(","));
      formData.append("category", category);
      
      // Add new files only - backend will keep existing files if no new files uploaded
      files.forEach((file) => {
        formData.append("files", file);
      });

      console.log("🔍 EditPost FormData contents:");
      for (let pair of formData.entries()) {
        console.log(pair[0] + ': ' + pair[1]);
      }

      const response = await axios.put(buildApiUrl(`/api/posts/${post.id}`), formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      console.log("🔍 EditPost response:", response);

      if (response.status === 200) {
        onPostUpdated(response.data);
      }
    } catch (error) {
      console.error("Error updating post:", error);
      alert("Error updating post. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(prev => [...(prev || []), ...selectedFiles]); // ✅ Append new files instead of replacing
  };

  const removeExistingFile = (fileUrl) => {
    setExistingFiles(existingFiles.filter(url => url !== fileUrl));
  };

  const toggleFormSelection = (form) => {
    setSelectedForms(prev => 
      prev.includes(form) 
        ? prev.filter(f => f !== form)
        : [...prev, form]
    );
  };

  const togglePupilSelection = (pupil) => {
    setTaggedPupils(prev => 
      prev.includes(pupil.school_id) 
        ? prev.filter(id => id !== pupil.school_id)
        : [...prev, pupil.school_id]
    );
  };

  const handleEmojiClick = (emojiObject) => {
    setContent(prev => prev + emojiObject.emoji);
    setShowEmojiPicker(false);
  };

  const getFileIcon = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    
    switch (ext) {
      case 'pdf':
        return <img src="/src/assets/icons/pdf-icon.svg" alt="PDF" className="w-10 h-10" />;
      case 'doc':
      case 'docx':
        return <img src="/src/assets/icons/word-icon.svg" alt="Word" className="w-10 h-10" />;
      case 'txt':
        return '📄';
      case 'xls':
      case 'xlsx':
      case 'csv':
        return '📊';
      case 'ppt':
      case 'pptx':
        return '📋';
      case 'zip':
      case 'rar':
      case '7z':
        return '🗜️';
      case 'mp3':
      case 'wav':
      case 'ogg':
        return '🎵';
      case 'mp4':
      case 'avi':
      case 'mov':
      case 'mkv':
        return '🎬';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'webp':
        return '🖼️';
      default:
        return '📎';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">✏️ Edit Post</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
          >
            ×
          </button>
        </div>

        <div className="overflow-y-auto max-h-[calc(90vh-140px)]">
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                📝 Post Title *
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter post title..."
                required
              />
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                🏷️ Category
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="school-announcement">📢 School Announcement</option>
                <option value="school-events">🎉 School Events</option>
                <option value="trips">🚌 Trips</option>
                <option value="weekly-letters">📝 Weekly Letters</option>
              </select>
            </div>

            {/* Content */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                📄 Content *
              </label>
              
              <ReactQuill
                theme="snow"
                value={content}
                onChange={setContent}
                modules={quillModules}
                formats={quillFormats}
                placeholder="Write your post content..."
                className="bg-white"
                style={{ height: '200px', marginBottom: '50px' }}
              />

              {/* Emoji Picker */}
              {showEmojiPicker && (
                <div className="relative mt-2 mb-4">
                  <div className="absolute z-10 bg-white border border-gray-200 rounded-lg shadow-lg">
                    <EmojiPicker 
                      onEmojiClick={handleEmojiClick}
                      width={300}
                      height={350}
                      previewConfig={{
                        showPreview: false
                      }}
                    />
                  </div>
                  {/* Overlay to close picker when clicking outside */}
                  <div 
                    className="fixed inset-0 z-0" 
                    onClick={() => setShowEmojiPicker(false)}
                  />
                </div>
              )}
              
              <div className="flex items-center justify-between mt-2">
                <button
                  type="button"
                  onClick={() => setShowEmojiPicker((prev) => !prev)}
                  className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                >
                  😊 Add Emoji
                </button>
              </div>
            </div>

            {/* Target Forms */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                🎓 Target Forms (leave empty for whole school)
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-3">
                {targetForms?.map((form) => (
                  <label key={form} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedForms.includes(form)}
                      onChange={() => toggleFormSelection(form)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">{form}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Pupil Tagging */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  🎯 Tag Specific Pupils (Optional)
                </label>
                <button
                  type="button"
                  onClick={() => setShowPupilTagging(!showPupilTagging)}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  {showPupilTagging ? "Hide" : "Show"} Pupil Tagging
                </button>
              </div>
              
              {showPupilTagging && (
                <div className="border border-gray-200 rounded-lg p-4 space-y-3">
                  <input
                    type="text"
                    value={pupilSearchTerm}
                    onChange={(e) => setPupilSearchTerm(e.target.value)}
                    placeholder="Search pupils by name..."
                    className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  />
                  
                  {taggedPupils.length > 0 && (
                    <div className="mb-3">
                      <p className="text-sm font-medium text-gray-700 mb-2">Tagged Pupils:</p>
                      <div className="flex flex-wrap gap-2">
                        {taggedPupils.map((pupilId) => {
                          const pupil = availablePupils.find(p => p.school_id === pupilId);
                          return (
                            <span
                              key={pupilId}
                              className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs flex items-center gap-1"
                            >
                              {pupil?.full_name || pupilId}
                              <button
                                type="button"
                                onClick={() => setTaggedPupils(prev => prev.filter(id => id !== pupilId))}
                                className="text-blue-600 hover:text-blue-800 ml-1"
                              >
                                ×
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  <div className="max-h-40 overflow-y-auto">
                    {loadingPupils ? (
                      <p className="text-sm text-gray-500">Loading pupils...</p>
                    ) : availablePupils.length > 0 ? (
                      <div className="grid grid-cols-1 gap-1">
                        {availablePupils
                          .filter(pupil => !taggedPupils.includes(pupil.school_id))
                          .map((pupil) => (
                            <label key={pupil.school_id} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                              <input
                                type="checkbox"
                                checked={taggedPupils.includes(pupil.school_id)}
                                onChange={() => togglePupilSelection(pupil)}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <span className="text-sm text-gray-700">
                                {pupil.full_name} ({pupil.form}) - ID: {pupil.school_id}
                              </span>
                            </label>
                          ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">No pupils found. Try searching...</p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Existing Files */}
            {existingFiles.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  📎 Current Files
                </label>
                <div className="space-y-2">
                  {existingFiles.map((fileUrl, index) => {
                    const fileName = fileUrl.split('/').pop() || fileUrl;
                    return (
                      <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded border">
                        <div className="flex items-center gap-2">
                          <span>{getFileIcon(fileName)}</span>
                          <span className="text-sm text-gray-700">{fileName}</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeExistingFile(fileUrl)}
                          className="text-red-600 hover:text-red-800 text-sm"
                        >
                          Remove
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* New Files */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                📎 Add New Files (Optional)
              </label>
              <input
                type="file"
                multiple
                accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                onChange={handleFileChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              {files.length > 0 && (
                <div className="mt-2">
                  <p className="text-sm text-gray-600">New files to add:</p>
                  <ul className="text-sm text-gray-700">
                    {files.map((file, index) => (
                      <li key={index}>• {file.name}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Submit Buttons */}
            <div className="flex gap-4 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
              >
                {isSubmitting ? "Updating..." : "Update Post"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditPost;
